import { CheckCircle2, XCircle, AlertTriangle, Database } from "lucide-react";

const ORDER = [["companies", "Company database"], ["people", "People data"], ["search", "Web search"]];

const detailFor = (s) => {
  if (s.errors?.length) return s.errors[0];
  if (!s.enabled) return "disabled in Settings";
  if (s.credentialStatus === "missing") return "no key configured";
  if (s.fallbacks) return `fell back to keyless search ${s.fallbacks}×`;
  if (s.credentialStatus === "none-required") return "keyless fallback — rate-limited, fewer results";
  return "unavailable during this hunt";
};

// Red = a required source failed (results are incomplete); amber = degraded or optional; green = healthy.
const toneFor = (key, s) => {
  if (s.ok) return "ok";
  if (key === "people") return "warn";
  if (key === "search" && !s.errors?.length) return "warn";
  return "down";
};

export const sourceIssues = (status) =>
  ORDER.filter(([k]) => status?.[k] && !status[k].ok).map(([k, fallback]) => ({ key: k, tone: toneFor(k, status[k]), label: status[k].label || fallback, detail: detailFor(status[k]) }));

const ICON = { ok: CheckCircle2, warn: AlertTriangle, down: XCircle };
const COLOR = { ok: "text-emerald-400", warn: "text-amber-300", down: "text-red-400" };
const PILL = { ok: "border-emerald-500/20 bg-emerald-500/[0.06]", warn: "border-amber-500/25 bg-amber-500/[0.06]", down: "border-red-500/30 bg-red-500/[0.08]" };

// Compact strip under the hunt header: one pill per source the run depended on, mirroring the onboarding preview.
export const HuntSourceStatus = ({ status, compact = false, idPrefix = "hunt-source" }) => {
  if (!status) return null;
  const rows = ORDER.filter(([k]) => status[k]).map(([k, fallback]) => ({ key: k, s: status[k], tone: toneFor(k, status[k]), label: status[k].label || fallback }));
  const down = rows.filter((r) => r.tone === "down");
  return (
    <div className="mt-2" data-testid={`${idPrefix}-status`}>
      <div className="flex flex-wrap gap-1.5">
        {rows.map(({ key, s, tone, label }) => {
          const Icon = ICON[tone];
          return (
            <span key={key} title={tone === "ok" ? `${label}: healthy` : `${label}: ${detailFor(s)}`} data-testid={`${idPrefix}-${key}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${PILL[tone]} ${tone === "ok" ? "text-gray-300" : COLOR[tone]}`}>
              <Icon className={`h-3 w-3 ${COLOR[tone]}`} />{label}
            </span>
          );
        })}
      </div>
      {!compact && down.length > 0 && (
        <p className="text-[11px] text-red-300 mt-1.5 flex items-start gap-1" data-testid={`${idPrefix}-warning`}>
          <Database className="h-3 w-3 mt-0.5 shrink-0" />
          <span>Results are incomplete — {down.map((r) => `${r.label} (${detailFor(r.s)})`).join("; ")}. Fix it under Settings → Data sources, then run the hunt again.</span>
        </p>
      )}
    </div>
  );
};
